/**
 * F-148 §10.2 `/stock/receiving/new` (AC1): supplier (existing or new) and an optional reference.
 * The create request id is generated once per mount and reused on retries (§3.2 idempotency).
 * `F148_DRAFT_EXISTS` / `F148_IDEMPOTENCY_KEY_REUSED` / `F148_REFERENCE_ALREADY_RECEIVED` link to
 * the existing draft/receipt.
 */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useAuthSession, useTranslation } from '@dukanti/shared';
import {
  useCreateReceivingDraft,
  useReceivingAccess,
  useReceivingSuppliers,
  useStockReceipt,
} from '@dukanti/shared/hooks';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { Button } from '@/components/ui/button';
import {
  DraftHeaderForm,
  ReceivingNotAvailable,
  ReceivingPausedBanner,
  referenceValidationMessageKey,
  type HeaderFormValue,
} from '@/features/stock/receiving/components';
import { createIdempotencyKey } from '@/features/stock/receiving/lib/idempotency-store';
import {
  getStockReceivingErrorInfo,
  receivingErrorKey,
  receivingErrorOptions,
} from '@/features/stock/receiving/lib/error-messages';

export function ReceivingNewRoute(): React.JSX.Element {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const access = useReceivingAccess(supabase, storeId);
  const linkedReceiptId = searchParams.get('linkedReceiptId');
  const linkedReceipt = useStockReceipt(supabase, linkedReceiptId);

  const [header, setHeader] = useState<HeaderFormValue>({
    supplier: null,
    shipmentReference: null,
    linkedReceiptId,
    note: null,
  });
  const [supplierName, setSupplierName] = useState('');
  const [supplierQuery, setSupplierQuery] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [clientRequestId] = useState(() => createIdempotencyKey());

  const suppliers = useReceivingSuppliers(supabase, storeId, supplierQuery);
  const create = useCreateReceivingDraft(supabase);

  const canManage = access.data?.canManage ?? false;
  const enabled = access.data?.enabled ?? false;
  const referenceError = referenceValidationMessageKey(header.shipmentReference);

  const submit = async (): Promise<void> => {
    setShowErrors(true);
    if (!storeId || !header.supplier || referenceError) return;
    try {
      const draft = await create.mutateAsync({
        storeId,
        clientRequestId,
        header: {
          supplier: header.supplier,
          shipmentReference: header.shipmentReference,
          linkedReceiptId: header.linkedReceiptId,
          note: header.note,
        },
      });
      navigate(`/stock/receiving/drafts/${encodeURIComponent(draft.id)}`, { replace: true });
    } catch {
      // Surfaced through `create.error` below.
    }
  };

  const errorInfo = create.error ? getStockReceivingErrorInfo(create.error) : null;
  // `F148_IDEMPOTENCY_KEY_REUSED` means the first create succeeded but its response was lost and
  // the header was edited since; the server returns that draft's id, so link to it like an
  // existing draft.
  const existingDraftId =
    (errorInfo?.code === 'F148_DRAFT_EXISTS' ||
      errorInfo?.code === 'F148_IDEMPOTENCY_KEY_REUSED') &&
    typeof errorInfo.details.draftId === 'string'
      ? errorInfo.details.draftId
      : null;
  const existingReceiptId =
    errorInfo?.code === 'F148_REFERENCE_ALREADY_RECEIVED' &&
    typeof errorInfo.details.receiptId === 'string'
      ? errorInfo.details.receiptId
      : null;

  return (
    <main
      dir={dir}
      data-testid="receiving-new-route"
      className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 lg:px-8"
    >
      <Link
        to="/stock/receiving"
        className="text-muted-foreground hover:text-foreground mb-4 inline-flex min-h-11 items-center gap-1 text-sm"
      >
        <ArrowLeft aria-hidden="true" className="size-4 rtl:rotate-180" />
        {t('admin.receiving.backToList')}
      </Link>
      <header className="mb-6 text-start">
        <h1 className="font-display text-foreground text-[28px] font-bold leading-tight tracking-tight">
          {t('admin.receiving.new.title')}
        </h1>
      </header>

      {access.isLoading ? (
        <div className="bg-admin-surface-muted h-32 animate-pulse rounded-lg motion-reduce:animate-none" />
      ) : null}

      {/* A failed access request is transient: never treated as "no permission" or "paused". */}
      {access.isError ? (
        <div
          role="alert"
          className="border-border bg-card shadow-admin-card rounded-lg border p-6 text-center"
          data-testid="receiving-access-error"
        >
          <p className="text-muted-foreground mb-4 text-sm">{t('common.errors.networkFailed')}</p>
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            onClick={() => void access.refetch()}
            data-testid="receiving-access-retry"
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {access.data && !canManage ? <ReceivingNotAvailable /> : null}
      {access.data && canManage && !enabled ? <ReceivingPausedBanner draftsReadOnly /> : null}

      {access.data && canManage && enabled ? (
        <form
          className="border-border bg-card shadow-admin-card space-y-6 rounded-lg border p-4 sm:p-6"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          noValidate
        >
          <DraftHeaderForm
            value={header}
            supplierName={supplierName}
            onChange={(patch, name) => {
              setHeader((current) => ({ ...current, ...patch }));
              if (name !== undefined) setSupplierName(name);
            }}
            supplierQuery={supplierQuery}
            onSupplierQueryChange={setSupplierQuery}
            suppliers={suppliers.data ?? []}
            suppliersLoading={suppliers.isFetching}
            linkedReceiptLabel={linkedReceipt.data?.displayReference ?? null}
            disabled={create.isPending}
            showSupplierError={showErrors}
          />

          {create.error ? (
            <div
              role="alert"
              className="border-destructive/40 bg-destructive/10 space-y-2 rounded-lg border p-3 text-start text-sm"
              data-testid="create-error"
            >
              <p className="text-destructive">
                {t(receivingErrorKey(create.error), receivingErrorOptions(create.error))}
              </p>
              {existingDraftId ? (
                <Link
                  to={`/stock/receiving/drafts/${encodeURIComponent(existingDraftId)}`}
                  className="text-foreground inline-flex min-h-11 items-center underline underline-offset-4"
                  data-testid="create-error-existing-draft"
                >
                  {t('admin.receiving.list.open')}
                </Link>
              ) : null}
              {existingReceiptId ? (
                <Link
                  to={`/stock/receiving/receipts/${encodeURIComponent(existingReceiptId)}`}
                  className="text-foreground inline-flex min-h-11 items-center underline underline-offset-4"
                  data-testid="create-error-existing-receipt"
                >
                  {t('admin.receiving.draft.openReceipt')}
                </Link>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              onClick={() => navigate('/stock/receiving')}
              disabled={create.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              className="min-h-11"
              disabled={create.isPending || !storeId}
              data-testid="create-draft-submit"
            >
              {create.isPending ? (
                <Loader2
                  aria-hidden="true"
                  className="size-4 animate-spin motion-reduce:animate-none"
                />
              ) : null}
              {create.isPending
                ? t('admin.receiving.new.creating')
                : t('admin.receiving.new.create')}
            </Button>
          </div>
        </form>
      ) : null}
    </main>
  );
}
